import Grid from '../../Generic/GridLegacyCompat';
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@mui/material';
import {
  CombinedFamilyInfo,
  ArrangementPolicy,
  ChildInvolvement,
} from '../../GeneratedClient';
import { ValidateDatePicker } from '../../Generic/Forms/ValidateDatePicker';
import { useParams } from 'react-router-dom';
import { useBackdrop } from '../../Hooks/useBackdrop';
import { useV1CasesModel } from '../../Model/V1CasesModel';
import { useVisibleFamilies } from '../../Model/Data';
import { useLocationConfiguration } from '../../Model/ConfigurationModel';
import { isBackdropClick } from '../../Utilities/handleBackdropClick';
import {
  getAutomaticPolicyVersion,
  getAvailablePolicyVersions,
  hasPolicyVersions,
  withPolicyVersion,
} from './arrangementPolicyVersions';

interface CreateArrangementDialogProps {
  v1CaseId: string;
  arrangementPolicy: ArrangementPolicy;
  onClose: () => void;
}

export function CreateArrangementDialog({
  v1CaseId,
  arrangementPolicy,
  onClose,
}: CreateArrangementDialogProps) {
  const { familyId } = useParams<{ familyId: string }>();
  const visibleFamilies = useVisibleFamilies();
  const family = visibleFamilies.find(
    (x) => x.family?.id === familyId
  ) as CombinedFamilyInfo;

  const arrangementReasons = useLocationConfiguration()?.arrangementReasons;
  const isReasonRequired = arrangementReasons && arrangementReasons.length > 0;
  const availablePolicyVersions = getAvailablePolicyVersions(arrangementPolicy);
  const policyHasVersions = hasPolicyVersions(arrangementPolicy);

  const [fields, setFields] = useState({
    requestedAtLocal: null as Date | null,
    partneringFamilyPersonId: null as string | null,
    reason: null as string | null,
    arrangementPolicyVersion: getAutomaticPolicyVersion(arrangementPolicy),
  });
  const {
    requestedAtLocal,
    partneringFamilyPersonId,
    reason,
    arrangementPolicyVersion,
  } = fields;

  const selectedPolicyVersion = availablePolicyVersions.find(
    (policyVersion) => policyVersion.version === arrangementPolicyVersion
  );
  const effectiveArrangementPolicy = selectedPolicyVersion
    ? withPolicyVersion(arrangementPolicy, selectedPolicyVersion)
    : arrangementPolicy;

  const activeAdults = family
    .family!.adults!.filter((adult) => adult.item1!.active)
    .map((adult) => adult.item1!);
  const children = family.family!.children!;

  const applicableFamilyMembers =
    effectiveArrangementPolicy.childInvolvement ===
    ChildInvolvement.NoChildInvolvement
      ? activeAdults
      : effectiveArrangementPolicy.childInvolvement ===
          ChildInvolvement.ChildOrAdultInvolvement
        ? activeAdults.concat(children)
        : children;

  const [dobError, setDobError] = useState(false);

  const v1CasesModel = useV1CasesModel();

  const withBackdrop = useBackdrop();

  async function save() {
    await withBackdrop(async () => {
      if (!partneringFamilyPersonId) {
        alert('A partnering family member was not selected. Try again.');
      } else if (requestedAtLocal == null) {
        alert('A date is required.');
      } else if (isReasonRequired && (reason == null || reason.length == 0)) {
        alert('A reason for the request is required.');
      } else if (policyHasVersions && !arrangementPolicyVersion) {
        alert('A policy version is required.');
      } else {
        await v1CasesModel.createArrangement(
          family.family?.id as string,
          v1CaseId,
          arrangementPolicy.arrangementType!,
          requestedAtLocal,
          partneringFamilyPersonId,
          reason && reason.length > 0 ? reason : null,
          arrangementPolicyVersion
        );
        //TODO: Error handling (start with a basic error dialog w/ request to share a screenshot, and App Insights logging)
        onClose();
      }
    });
  }

  return (
    <Dialog
      open={true}
      onClose={(_, reason: string) => (!isBackdropClick(reason) ? onClose : {})}
      scroll="body"
      aria-labelledby="create-arrangement-title"
    >
      <DialogTitle id="create-arrangement-title">
        Create {arrangementPolicy.arrangementType}
      </DialogTitle>
      <DialogContent>
        <form noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid item>
              <ValidateDatePicker
                label="Requested at"
                value={requestedAtLocal}
                onChange={(date) =>
                  setFields({ ...fields, requestedAtLocal: date })
                }
                disableFuture
                onErrorChange={setDobError}
                textFieldProps={{
                  size: 'small',
                  required: true,
                  sx: { marginTop: 1 },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl required component="fieldset">
                <FormLabel component="legend">Family Member:</FormLabel>
                <RadioGroup
                  aria-label="familyMember"
                  name="familyMember"
                  value={partneringFamilyPersonId}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      partneringFamilyPersonId: e.target.value,
                    })
                  }
                >
                  {applicableFamilyMembers.map((person) => (
                    <FormControlLabel
                      key={person.id}
                      value={person.id}
                      control={<Radio size="small" />}
                      label={person.firstName!}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            {arrangementReasons && arrangementReasons.length > 0 && (
              <Grid item xs={12} sm={6}>
                <FormControl required fullWidth size="small">
                  <InputLabel id="arrangement-reason-label">
                    Reason for Request
                  </InputLabel>
                  <Select
                    labelId="arrangement-reason-label"
                    label="Reason for Request"
                    id="arrangement-reason"
                    value={reason || ''}
                    onChange={(e) =>
                      setFields({ ...fields, reason: e.target.value as string })
                    }
                  >
                    <MenuItem key="placeholder" value="" disabled>
                      Select a reason
                    </MenuItem>
                    {arrangementReasons.map((arrangementReason) => (
                      <MenuItem
                        key={arrangementReason}
                        value={arrangementReason}
                      >
                        {arrangementReason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {policyHasVersions && availablePolicyVersions.length > 1 && (
              <Grid item xs={12} sm={6}>
                <FormControl required fullWidth size="small">
                  <InputLabel id="arrangement-policy-version-label">
                    Policy Version
                  </InputLabel>
                  <Select
                    labelId="arrangement-policy-version-label"
                    label="Policy Version"
                    id="arrangement-policy-version"
                    value={arrangementPolicyVersion || ''}
                    onChange={(e) =>
                      setFields({
                        ...fields,
                        arrangementPolicyVersion: e.target.value as string,
                        partneringFamilyPersonId: null,
                      })
                    }
                  >
                    <MenuItem key="placeholder" value="" disabled>
                      Select a version
                    </MenuItem>
                    {availablePolicyVersions.map((policyVersion) => (
                      <MenuItem
                        key={policyVersion.version}
                        value={policyVersion.version}
                      >
                        {policyVersion.version}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={save}
          variant="contained"
          color="primary"
          disabled={
            !partneringFamilyPersonId ||
            (isReasonRequired && (!reason || reason.length === 0)) ||
            (policyHasVersions && !arrangementPolicyVersion) ||
            dobError
          }
        >
          Create Arrangement
        </Button>
      </DialogActions>
    </Dialog>
  );
}
